/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */
import {ChangeEvent, forwardRef, ForwardedRef, InputHTMLAttributes} from 'react';
import * as styled from 'styled-components';
import {paddings, theme} from 'components/shared/theme';
import {Label} from '../label';

export interface IInput extends InputHTMLAttributes<HTMLInputElement> {
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  icon?: JSX.Element;
  imagePath?: string;
  imageAltText?: string;
  label?: string;
  labelColor?: string;
  labelIcon?: JSX.Element;
  labelClassName?: string;
  helperText?: string;
  helperTextClassName?: string;
  id?: string;
  autocomplete?: string;
  ref?: ForwardedRef<HTMLInputElement>;
  width?: number | string;
}

const {
  colors,
  fontSizeS,
  formFieldWidth,
  formFieldMaxWidth,
  primaryBorderColor,
  primaryBorderRadius,
  primaryFontSize,
  primaryLineHeight,
  primaryInputHeight,
  inputIconWidth,
  spacing
} = theme;

export const InputElement = styled.default.input<IInput>`
  background-color: ${colors.white};
  border: 1px solid ${({error}) => error ? colors.lightRed : primaryBorderColor};
  border-radius: ${primaryBorderRadius};
  display: block;
  font-size: ${primaryFontSize};
  height: ${primaryInputHeight};
  line-height: ${primaryLineHeight};
  outline: none;
  padding: ${paddings.small} ${({icon, imagePath}) => (icon || imagePath) ? paddings.xlarge : paddings.small};
  transition: border-color .15s ease-in-out, box-shadow .15s ease-in-out;
  background-position: 1rem center;
  background-repeat: no-repeat;
  width: inherit;
  opacity: ${({disabled}) => disabled ? 0.8 : 1};
  cursor: ${({disabled}) => disabled && 'not-allowed'};
  -webkit-text-fill-color: ${({disabled}) => disabled && colors.gray800};
`;
const HelperText = styled.default.p<IInput>`
  color: ${({error}) => error ? colors.lightRed : colors.gray600};
  font-weight: 400;
  font-size: ${fontSizeS};
  margin-top: ${spacing.xxSmall};
`;
const ImageWrapper = styled.default.div<IInput>`
  width: ${inputIconWidth}px;
  height: ${inputIconWidth}px;
  z-index: 1;
  top: calc(50% - ${inputIconWidth / 2}px);
  left: 8px;
  position: absolute;
  display: flex;
  align-items: center;

  & img, svg {
    width: ${inputIconWidth}px;
    height: ${inputIconWidth}px;
  }
`;
const InputWrapper = styled.default.div<IInput>`
  width: 100%;
  position: relative;
`;

export const InputComponentWrapper = styled.default.div<IInput>`
  position: relative;
  width: ${({width}) => width && isNaN(+width) ? width : ((width || formFieldWidth) + 'px')};
  max-width: ${formFieldMaxWidth}px;
  display: flex;
  flex-direction: column;
`;
export const InputComponent = forwardRef((
  {
    label,
    labelColor,
    labelIcon,
    labelClassName,
    icon,
    imagePath,
    imageAltText = '',
    helperText,
    helperTextClassName,
    error,
    width,
    disabled,
    name,
    ...props
  }: IInput, ref: ForwardedRef<HTMLInputElement>): JSX.Element => {
  const InputIcon = icon || (!!imagePath && (<img src={imagePath} alt={imageAltText} />)) || null;

  return (
    <InputComponentWrapper width={width}>
      {!!label && (
        <Label
          text={label}
          color={labelColor}
          icon={labelIcon}
          className={labelClassName}
        />
      )}
      <InputWrapper>
        <ImageWrapper>
          {InputIcon}
        </ImageWrapper>
        <InputElement
          icon={icon}
          name={name}
          imagePath={imagePath}
          disabled={disabled}
          ref={ref}
          {...props}
        />
      </InputWrapper>
      {!!helperText && <HelperText className={helperTextClassName} error={error}>{helperText}</HelperText>}
    </InputComponentWrapper>
  );
});

export const InputWithRef = forwardRef((props: IInput, ref: ForwardedRef<HTMLInputElement>) => (
  <InputComponent {...props} ref={ref}/>
));

InputWithRef.displayName = 'InputWithRef';

export default InputComponent;